import { STATUS_CHIP_VARIANTS } from "@/components_v2/StatusChip/constants";
import { MODERATION_PATH } from "@/domains/moderation/constants";
import type {
  AnswerVerdict,
  AttemptState,
  CertificationInvitationStatus,
  CertificationLifecycleStatus,
  ContentItemType,
  ContentLockReason,
  ContributionGradeState,
  ContributionKind,
  ContributionVerdict,
  EnrollmentState,
  HeartbeatRejectReason,
  IssuanceState,
  ParticipantStatusFilter,
  PassingRule,
  RequirementType,
  ReviewVerdict,
} from "@/redux/certifications/types";
import type {
  BanState,
  CertificationStatus,
  ContributionStatus,
  EnrollmentAction,
  InvitationStatus,
  KnowledgeCheckState,
  ModuleStatus,
  ParticipantStatus,
  StatusChipConfig,
} from "@/domains/certifications/types";

export const STATUS_CHIPS: Record<CertificationStatus, StatusChipConfig> = {
  certified: {
    labelKey: "certification.labels.status.certified",
    variant: STATUS_CHIP_VARIANTS.NAVY,
    hasCheckIcon: true,
  },
  awaitingIssue: {
    labelKey: "certification.labels.status.awaitingIssue",
    variant: STATUS_CHIP_VARIANTS.BLUE,
  },
  inProgress: {
    labelKey: "certification.labels.status.inProgress",
    variant: STATUS_CHIP_VARIANTS.AMBER,
  },
  notStarted: {
    labelKey: "certification.labels.status.notStarted",
    variant: STATUS_CHIP_VARIANTS.NEUTRAL,
  },
  withdrawn: {
    labelKey: "certification.labels.status.withdrawn",
    variant: STATUS_CHIP_VARIANTS.RED,
  },
  withdrawnBan: {
    labelKey: "certification.labels.status.withdrawnBan",
    variant: STATUS_CHIP_VARIANTS.RED,
  },
  pending: {
    labelKey: "certification.labels.status.pending",
    variant: STATUS_CHIP_VARIANTS.AMBER,
  },
  accepted: {
    labelKey: "certification.labels.status.accepted",
    variant: STATUS_CHIP_VARIANTS.NAVY,
    hasCheckIcon: true,
  },
};

export const CERTIFICATION_LIFECYCLE_STATUSES = {
  DRAFT: "DRAFT",
  UPCOMING: "UPCOMING",
  ACTIVE: "ACTIVE",
  CLOSED: "CLOSED",
  ARCHIVED: "ARCHIVED",
} as const satisfies Record<
  CertificationLifecycleStatus,
  CertificationLifecycleStatus
>;

export const ENROLLMENT_STATES = {
  ACTIVE: "ACTIVE",
  WITHDRAWN: "WITHDRAWN",
} as const satisfies Record<EnrollmentState, EnrollmentState>;

export const ISSUANCE_STATES = {
  QUEUED: "QUEUED",
  ISSUING: "ISSUING",
  ISSUED: "ISSUED",
  FAILED: "FAILED",
  CANCELLED: "CANCELLED",
} as const satisfies Record<IssuanceState, IssuanceState>;

export const CONTENT_ITEM_TYPES = {
  VIDEO: "VIDEO",
  RESOURCE: "RESOURCE",
  KNOWLEDGE_CHECK: "KNOWLEDGE_CHECK",
  COMMUNITY: "COMMUNITY",
  LIVE_SESSION: "LIVE_SESSION",
} as const satisfies Record<ContentItemType, ContentItemType>;

export const REQUIREMENT_TYPES = {
  VIDEO_COMPLETION: "VIDEO_COMPLETION",
  KNOWLEDGE_CHECK_PASS: "KNOWLEDGE_CHECK_PASS",
  COMMUNITY_POST_COUNT: "COMMUNITY_POST_COUNT",
  COMMUNITY_COMMENT_COUNT: "COMMUNITY_COMMENT_COUNT",
  RESOURCE_DOWNLOAD: "RESOURCE_DOWNLOAD",
} as const satisfies Record<RequirementType, RequirementType>;

export const CONTENT_LOCK_REASONS = {
  NOT_STARTED: "NOT_STARTED",
  MODULE_LOCKED: "MODULE_LOCKED",
  TRACK_LOCKED: "TRACK_LOCKED",
  ITEM_LOCKED: "ITEM_LOCKED",
} as const satisfies Record<ContentLockReason, ContentLockReason>;

export const ATTEMPT_STATES = {
  SUBMITTED: "SUBMITTED",
  AI_REVIEWING: "AI_REVIEWING",
  PENDING_HUMAN_REVIEW: "PENDING_HUMAN_REVIEW",
  PASSED: "PASSED",
  REJECTED: "REJECTED",
} as const satisfies Record<AttemptState, AttemptState>;

export const PASSING_RULES = {
  ALL_CORRECT: "ALL_CORRECT",
  AT_LEAST_N: "AT_LEAST_N",
} as const satisfies Record<PassingRule, PassingRule>;

export const ANSWER_VERDICTS = {
  CORRECT: "CORRECT",
  INCORRECT: "INCORRECT",
  INCONCLUSIVE: "INCONCLUSIVE",
} as const satisfies Record<AnswerVerdict, AnswerVerdict>;

export const CONTRIBUTION_GRADE_STATES = {
  SUBMITTED: "SUBMITTED",
  AI_REVIEWING: "AI_REVIEWING",
  PENDING_HUMAN_REVIEW: "PENDING_HUMAN_REVIEW",
  ACCEPTED: "ACCEPTED",
  REJECTED: "REJECTED",
  WITHDRAWN: "WITHDRAWN",
} as const satisfies Record<ContributionGradeState, ContributionGradeState>;

export const CONTRIBUTION_VERDICTS = {
  ON_TOPIC: "ON_TOPIC",
  OFF_TOPIC: "OFF_TOPIC",
  INCONCLUSIVE: "INCONCLUSIVE",
} as const satisfies Record<ContributionVerdict, ContributionVerdict>;

export const CONTRIBUTION_KINDS = {
  POST: "post",
  COMMENT: "comment",
} as const satisfies Record<string, ContributionKind>;

export const MAX_PLAYBACK_RATE = 1.25;
export const PLAYBACK_RATES = [1, MAX_PLAYBACK_RATE];

export const HEARTBEAT_REJECT_REASONS = {
  RATE_TOO_HIGH: "RATE_TOO_HIGH",
  AHEAD_OF_WALL_CLOCK: "AHEAD_OF_WALL_CLOCK",
  SPAN_TOO_LONG: "SPAN_TOO_LONG",
  OUT_OF_BOUNDS: "OUT_OF_BOUNDS",
  SESSION_CLOSED: "SESSION_CLOSED",
  DUPLICATE_SEQUENCE: "DUPLICATE_SEQUENCE",
  RATE_LIMITED: "RATE_LIMITED",
  SESSION_BUDGET_EXCEEDED: "SESSION_BUDGET_EXCEEDED",
} as const satisfies Record<HeartbeatRejectReason, HeartbeatRejectReason>;

export const CERTIFICATION_INVITATION_STATUSES = {
  PENDING: "PENDING",
  ENROLLED: "ENROLLED",
} as const satisfies Record<
  CertificationInvitationStatus,
  CertificationInvitationStatus
>;

export const PARTICIPANT_STATUS_FILTERS = {
  NOT_STARTED: "NOT_STARTED",
  IN_PROGRESS: "IN_PROGRESS",
  AWAITING_REVIEW: "AWAITING_REVIEW",
} as const satisfies Record<ParticipantStatusFilter, ParticipantStatusFilter>;

export const MODULE_STATUSES = {
  COMPLETE: "complete",
  CURRENT: "current",
  AVAILABLE: "available",
  LOCKED: "locked",
} as const satisfies Record<string, ModuleStatus>;

export const KNOWLEDGE_CHECK_STATES = {
  NOT_STARTED: "notStarted",
  UNDER_REVIEW: "underReview",
  ANOTHER_ROUND: "anotherRound",
  PASSED: "passed",
  FAILED: "failed",
} as const satisfies Record<string, KnowledgeCheckState>;

export const CONTRIBUTION_STATUSES = {
  UNDER_REVIEW: "underReview",
  REMOVED: "removed",
  COUNTING_PENDING: "countingPending",
  COUNTS: "counts",
  DOES_NOT_COUNT: "doesNotCount",
} as const satisfies Record<string, ContributionStatus>;

export const PARTICIPANT_STATUSES = {
  CERTIFIED: "certified",
  AWAITING_ISSUE: "awaitingIssue",
  IN_PROGRESS: "inProgress",
  NOT_STARTED: "notStarted",
  WITHDRAWN: "withdrawn",
  WITHDRAWN_BAN: "withdrawnBan",
} as const satisfies Record<string, ParticipantStatus>;

export const BAN_KINDS = {
  TIMED: "timed",
  PERMANENT: "permanent",
} as const satisfies Record<string, BanState["kind"]>;

export const ENROLLMENT_ACTIONS = {
  WITHDRAW: "withdraw",
  REINSTATE: "reinstate",
} as const satisfies Record<string, EnrollmentAction>;

export const LOCK_REASON_KEYS: Record<ContentLockReason, string> = {
  NOT_STARTED: "certification.labels.lockReason.notStarted",
  MODULE_LOCKED: "certification.labels.lockReason.moduleLocked",
  TRACK_LOCKED: "certification.labels.lockReason.trackLocked",
  ITEM_LOCKED: "certification.labels.lockReason.itemLocked",
};

export const UNLOCK_STEP_KEYS: Record<RequirementType, string> = {
  VIDEO_COMPLETION: "certification.labels.unlockStep.videoCompletion",
  KNOWLEDGE_CHECK_PASS: "certification.labels.unlockStep.knowledgeCheckPass",
  COMMUNITY_POST_COUNT: "certification.labels.unlockStep.communityPostCount",
  COMMUNITY_COMMENT_COUNT:
    "certification.labels.unlockStep.communityCommentCount",
  RESOURCE_DOWNLOAD: "certification.labels.unlockStep.resourceDownload",
};

export const INVITATION_STATUSES: Record<
  CertificationInvitationStatus,
  InvitationStatus
> = {
  PENDING: "pending",
  ENROLLED: "accepted",
};

export const REVIEW_VERDICTS: ReviewVerdict[] = [
  ANSWER_VERDICTS.CORRECT,
  ANSWER_VERDICTS.INCORRECT,
];

export const REVIEW_VERDICT_LABEL_KEYS: Record<ReviewVerdict, string> = {
  CORRECT: "certification.labels.verdict.correct",
  INCORRECT: "certification.labels.verdict.incorrect",
};

export const PROGRAMME_CONTACT_EMAIL = "commons@unprme.org";
export const PROGRAMME_CONTACT_HREF = `mailto:${PROGRAMME_CONTACT_EMAIL}`;

export const COMMUNITY_MODERATION_PATH: string | null = MODERATION_PATH;
